/* Aufnahme im Browser (MediaRecorder) für Pia und für das Diktieren in jedes Textfeld (Diktat.js). Erkennung NUR über den eigenen Server
   (Whisper-Container) – keine Browser-Spracherkennung, das Audio wird nirgends gespeichert. Ein Hook, zwei Aufrufer. */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';

export const MAX_SEKUNDEN = 120;
const mm = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
export const zeit = mm;

function mikrofonHinweis() {
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
  return ios
    ? 'Das Mikrofon ist nicht erlaubt. Am iPhone: Einstellungen › Safari › Mikrofon › Erlauben (oder Einstellungen › Datenschutz › Mikrofon), dann die Seite neu laden.'
    : 'Das Mikrofon ist nicht erlaubt. Am Mac: Systemeinstellungen › Datenschutz & Sicherheit › Mikrofon, deinen Browser anhaken und in der Adressleiste das Mikrofon für diese Seite erlauben.';
}

/* zustand: ruhig | aufnahme | erkennen. `onText(text, modell)` bekommt das Ergebnis. */
export function useSprachaufnahme({ onText, pfad = '/assistenz/sprache', felder = null, dateiname = 'pia' }) {
  const [zustand, setZustand] = useState('ruhig');
  const [sek, setSek] = useState(0);
  const [pegel, setPegel] = useState(0);
  const [fehler, setFehler] = useState('');
  const rec = useRef(null);
  const teile = useRef([]);
  const timer = useRef(null);
  const audio = useRef({ ctx: null, raf: 0, stream: null });
  const verwerfen = useRef(false);
  const neueste = useRef({});                                 // aktuelle Werte, damit eine laufende Aufnahme nicht mit alten Rückrufen endet
  neueste.current = { onText, felder, pfad, dateiname };

  const aufraeumen = useCallback(() => {
    clearInterval(timer.current);
    cancelAnimationFrame(audio.current.raf);
    try { audio.current.stream?.getTracks().forEach((t) => t.stop()); } catch (e) { /* egal */ }
    try { audio.current.ctx?.close(); } catch (e) { /* egal */ }
    audio.current = { ctx: null, raf: 0, stream: null };
    setPegel(0);
  }, []);

  const stoppen = useCallback((abbrechen) => {
    verwerfen.current = !!abbrechen;
    try { if (rec.current && rec.current.state !== 'inactive') rec.current.stop(); else aufraeumen(); } catch (e) { aufraeumen(); }
  }, [aufraeumen]);

  useEffect(() => () => { verwerfen.current = true; try { if (rec.current?.state !== 'inactive') rec.current?.stop(); } catch (e) { /* egal */ } aufraeumen(); }, [aufraeumen]);

  const erkennen = useCallback(async (blob) => {
    setZustand('erkennen');
    try {
      const endung = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm';
      const fd = new FormData();
      const n = neueste.current;
      fd.append('audio', new File([blob], `${n.dateiname}.${endung}`, { type: blob.type }));
      const extra = typeof n.felder === 'function' ? n.felder() : n.felder;
      if (extra) Object.entries(extra).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const { data } = await api.post(n.pfad, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 150000 });
      n.onText?.(data.text, data.modell, data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 422) setFehler('Ich habe nichts verstanden. Bitte noch einmal aufnehmen, etwas lauter und näher am Mikrofon.');
      else if (status === 413) setFehler('Die Aufnahme ist zu lang. Bitte kürzer sprechen (höchstens 2 Minuten).');
      else if (status === 403 || status === 404) setFehler('Spracheingabe ist für dich nicht verfügbar.');
      else if (status === 502 || status === 503 || !e?.response) setFehler('Der Spracherkennungsdienst ist gerade nicht erreichbar. Bitte tippen oder gleich noch einmal versuchen.');
      else setFehler(typeof detail === 'string' ? detail : 'Die Spracheingabe hat nicht geklappt.');
    }
    setZustand('ruhig');
  }, []);

  const start = useCallback(async () => {
    setFehler('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setFehler('Aufnahme geht in diesem Browser nicht. Bitte tippen.'); return;
    }
    let stream;
    try {                                                   // sauberer Ton für die Erkennung; nicht unterstützte Wünsche ignoriert der Browser
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1, sampleRate: 16000 } });
    } catch (e) {
      if (e?.name === 'OverconstrainedError' || e?.name === 'TypeError') {
        try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch (e2) { e = e2; }
      }
      if (!stream) { setFehler(e?.name === 'NotFoundError' ? 'Es wurde kein Mikrofon gefunden.' : mikrofonHinweis()); return; }
    }
    const typ = ['audio/webm;codecs=opus', 'audio/mp4'].find((t) => MediaRecorder.isTypeSupported?.(t)) || '';
    let r;
    try { r = new MediaRecorder(stream, typ ? { mimeType: typ } : undefined); }
    catch (e) { stream.getTracks().forEach((t) => t.stop()); setFehler('Aufnahme geht in diesem Browser nicht. Bitte tippen.'); return; }
    teile.current = []; verwerfen.current = false;
    r.ondataavailable = (ev) => { if (ev.data?.size) teile.current.push(ev.data); };
    r.onstop = () => {
      aufraeumen();
      if (verwerfen.current) { setZustand('ruhig'); return; }
      const blob = new Blob(teile.current, { type: (r.mimeType || typ || 'audio/webm').split(';')[0] });
      if (blob.size < 1500) { setFehler('Die Aufnahme war zu kurz. Bitte noch einmal.'); setZustand('ruhig'); return; }
      erkennen(blob);
    };
    rec.current = r;
    audio.current.stream = stream;
    try {                                                   // Pegelbalken: nur Anzeige, ohne Pegel läuft die Aufnahme trotzdem
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC(); const quelle = ctx.createMediaStreamSource(stream); const an = ctx.createAnalyser();
      an.fftSize = 512; quelle.connect(an); audio.current.ctx = ctx;
      const puffer = new Uint8Array(an.fftSize);
      const schleife = () => {
        an.getByteTimeDomainData(puffer);
        let max = 0; for (let i = 0; i < puffer.length; i++) max = Math.max(max, Math.abs(puffer[i] - 128));
        setPegel(Math.min(1, max / 64));
        audio.current.raf = requestAnimationFrame(schleife);
      };
      schleife();
    } catch (e) { /* kein Pegel */ }
    r.start(1000);
    setSek(0); setZustand('aufnahme');
    timer.current = setInterval(() => setSek((n) => { if (n + 1 >= MAX_SEKUNDEN) stoppen(false); return n + 1; }), 1000);
  }, [aufraeumen, erkennen, stoppen]);

  /* `laeuft`: gibt es eine Aufnahme, die man beenden könnte (für „Taste schon losgelassen, bevor das Mikrofon bereit war“). */
  return { zustand, sek, pegel, fehler, start, laeuft: () => rec.current?.state === 'recording', fertig: () => stoppen(false), abbrechen: () => stoppen(true), fehlerWeg: () => setFehler('') };
}
